import { useEffect, useRef, useState, type ComponentType, type CSSProperties } from "react";
import { Sparkles } from "lucide-react";
import { VISION_CARD_ICONS, VISION_CARD_IMAGES, VISION_CHIP_ICONS, VISION_IMAGE } from "@/mock";
import { useLanguage } from "@/i18n/LanguageContext";

type IconT = ComponentType<{ className?: string }>;

const TiltCard = ({
  icon: Icon,
  image,
  eyebrow,
  title,
  description,
  chips,
  chipIcons,
  testId,
}: {
  icon: IconT;
  image: string;
  eyebrow: string;
  title: string;
  description: string;
  chips: string[];
  chipIcons: IconT[];
  testId: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [t, setT] = useState({ rx: 0, ry: 0, gx: 50, gy: 50 });
  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    setT({ rx: (0.5 - py) * 10, ry: (px - 0.5) * 12, gx: px * 100, gy: py * 100 });
  };
  const onLeave = () => setT({ rx: 0, ry: 0, gx: 50, gy: 50 });
  return (
    <div className="reveal [perspective:1200px] h-full">
      <div
        ref={ref}
        data-testid={testId}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="group relative overflow-hidden rounded-2xl bg-[#1b3bd1] border border-white/15 transition-transform duration-200 ease-out will-change-transform h-full flex flex-col"
        style={{ transform: `rotateX(${t.rx}deg) rotateY(${t.ry}deg)`, transformStyle: "preserve-3d", boxShadow: "0 30px 60px -20px rgba(0,0,0,0.45)" }}
      >
        <div className="relative h-52 md:h-60 shrink-0">
          <div className="absolute inset-0 overflow-hidden">
            <img src={image} alt="" className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1b3bd1] via-[#1b3bd1]/30 to-transparent" />
            <div className="absolute inset-0 bg-[#2E4FE6]/25 mix-blend-multiply" />
          </div>
          <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 w-14 h-14 rounded-xl bg-[#F2641E] flex items-center justify-center shadow-lg z-10">
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-70"
          style={{ background: `radial-gradient(420px circle at ${t.gx}% ${t.gy}%, rgba(242,100,30,0.35), transparent 60%)` }}
        />
        <div className="relative px-4 sm:px-6 md:px-8 pt-12 pb-9 flex-1 flex flex-col">
          <div className="text-[#ffd9c4] font-bold text-[11px] tracking-[0.2em] uppercase mb-3">{eyebrow}</div>
          <h3 className="font-display text-2xl md:text-3xl uppercase text-white mb-3">{title}</h3>
          <div className="flex-1 flex items-center justify-center py-2 mb-6">
            <p className="text-white/80 leading-relaxed max-w-sm mx-auto">{description}</p>
          </div>
          <div className="flex flex-wrap xl:flex-nowrap justify-center gap-2">
            {chips.map((label, i) => {
              const Ci = chipIcons[i];
              return (
                <div key={i} className={`flex justify-center ${i === 0 ? "basis-full xl:basis-auto" : "basis-[calc(50%-4px)] sm:basis-auto"}`}>
                  <span className="inline-flex w-full sm:w-auto items-center justify-center gap-1.5 text-center leading-snug whitespace-normal sm:whitespace-nowrap bg-white/10 hover:bg-white/20 border border-white/15 rounded-2xl sm:rounded-full px-2.5 sm:px-3 py-2 text-xs sm:text-[13px] text-white transition-colors">
                    <Ci className="w-3.5 h-3.5 text-[#F2641E]" />
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const FloatShape = ({ className, style }: { className: string; style: CSSProperties }) => (
  <div className={`absolute pointer-events-none ${className}`} style={style} />
);

const Vision = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section id="vision" data-testid="vision-section" className="relative bg-[#2E4FE6] py-10 md:py-14 overflow-hidden [perspective:1000px]">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "30px 30px" }} />
      <FloatShape
        className="w-40 h-40 rounded-3xl bg-[#F2641E]/30 blur-[2px] animate-[spin_18s_linear_infinite]"
        style={{ top: "6%", right: "4%", transform: `translateY(${scrollY * 0.04}px) rotate(18deg)` }}
      />
      <FloatShape className="w-24 h-24 rounded-2xl border-2 border-white/25" style={{ top: "62%", left: "3%", transform: `translateY(${-scrollY * 0.03}px) rotate(-12deg)` }} />
      <FloatShape className="w-16 h-16 rounded-full bg-white/10" style={{ bottom: "10%", right: "12%", transform: `translateY(${scrollY * 0.05}px)` }} />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-8">
        <div className="flex flex-col items-center gap-10 mb-12 md:mb-16">
          <div className="reveal text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#F2641E]" />
              <span className="text-white font-bold text-[11px] tracking-[0.2em] uppercase">{t.vision.badge}</span>
            </div>
            <h2 data-testid="vision-headline" className="font-display text-[clamp(2.1rem,5.5vw,5rem)] uppercase text-white">
              {t.vision.title}
            </h2>
          </div>

          <div className="reveal relative mx-auto w-full max-w-[420px] md:max-w-[480px]" data-testid="vision-image">
            <div className="absolute -top-3 -left-3 w-16 h-16 border-t-4 border-l-4 border-[#F2641E] rounded-tl-xl pointer-events-none z-10" />
            <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-4 border-r-4 border-white/70 rounded-br-xl pointer-events-none z-10" />
            <div className="relative aspect-[2/3] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/15">
              <img src={VISION_IMAGE} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1b3bd1]/70 via-transparent to-transparent" />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {t.vision.cards.map((card, i) => (
            <TiltCard
              key={i}
              testId={`vision-card-${i}`}
              icon={VISION_CARD_ICONS[i]}
              image={VISION_CARD_IMAGES[i]}
              eyebrow={card.eyebrow}
              title={card.title}
              description={card.description}
              chips={card.chips}
              chipIcons={VISION_CHIP_ICONS[i]}
            />
          ))}
        </div>

        <div className="reveal mt-12 flex flex-wrap items-center justify-center gap-4" data-testid="vision-key-idea">
          <span className="text-[#F2641E] font-bold text-xs tracking-[0.22em] uppercase">{t.vision.keyIdeaLabel}</span>
          <span className="font-display text-3xl md:text-5xl uppercase text-white">{t.vision.keyIdeaValue}</span>
        </div>
      </div>
    </section>
  );
};

export default Vision;
